<template>
  <div>
    <!-- Hero -->
    <section class="hero">
      <div class="hero-bg"></div>
      <div class="container hero-content">
        <span class="label-tag">Football Tactics & Analysis</span>
        <h1>Chris Roughsedge</h1>
        <p class="hero-subtitle">
          Football Tactics Historian. Strategist. Analyst.
        </p>
        <p class="hero-description">
          Two decades of leadership. A lifetime in football. Data-driven insight
          that transforms how teams prepare, compete, and win.
        </p>
        <div class="hero-actions">
          <router-link to="/contact" class="btn-primary-custom">Get in Touch</router-link>
          <router-link to="/about" class="btn-outline-custom">Learn More</router-link>
        </div>
      </div>
    </section>

    <!-- Pillars -->
    <section class="section section-alt">
      <div class="container">
        <div class="text-center mb-5">
          <span class="label-tag">What I Bring</span>
          <h2>Where Football Meets Data</h2>
        </div>
        <div class="row g-4">
          <div class="col-md-4" v-for="(pillar, index) in pillars" :key="pillar.title">
            <div class="card-custom text-center">
              <div class="pillar-icon">
                <svg v-if="index === 0" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4A7CC9" stroke-width="1.5"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
                <svg v-else-if="index === 1" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4A7CC9" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
                <svg v-else width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4A7CC9" stroke-width="1.5"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              </div>
              <h3>{{ pillar.title }}</h3>
              <p>{{ pillar.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Credentials -->
    <section class="section section-dark">
      <div class="container">
        <div class="text-center mb-5">
          <span class="label-tag">Credentials</span>
          <h2>Recognised Expertise</h2>
        </div>
        <div class="row g-4">
          <div class="col-6 col-md-3" v-for="cred in credentials" :key="cred.label">
            <div class="credential-card text-center">
              <div class="credential-value">{{ cred.value }}</div>
              <div class="credential-label">{{ cred.label }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="section section-alt">
      <div class="container text-center">
        <h2>Ready to Elevate Your Analysis?</h2>
        <p class="section-subtitle mx-auto mt-3">
          Whether you're looking for tactical insight, opposition reports, or
          data-driven strategy, let's talk about how I can help your club succeed.
        </p>
        <router-link to="/contact" class="btn-primary-custom">Start a Conversation</router-link>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'HomeView',
  data() {
    return {
      pillars: [
        {
          title: 'Leadership & Strategy',
          description: 'Twenty years of leadership across football and technology. The strategic thinking to see the bigger picture and deliver results.'
        },
        {
          title: 'Tactical Analysis',
          description: 'Deep tactical breakdowns rooted in two decades of studying the game. From formation analysis to set-piece strategy, every detail is examined.'
        },
        {
          title: 'Data Analytics',
          description: 'Data-driven insight using StatsBomb, Wyscout, and GPS data. Turning raw numbers into actionable intelligence that informs decisions.'
        }
      ],
      credentials: [
        { value: 'PFSA', label: 'Certified Scout & Analyst' },
        { value: 'FC Barcelona', label: 'Executive Program & Tactical Diploma' },
        { value: 'English FA', label: 'Coaching Qualifications' },
        { value: '20+ Years', label: 'Leadership Experience' }
      ]
    }
  }
}
</script>

<style scoped>
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    160deg,
    var(--navy-dark) 0%,
    var(--navy) 40%,
    var(--navy-light) 100%
  );
}

.hero-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at 70% 50%,
    rgba(74, 124, 201, 0.08) 0%,
    transparent 70%
  );
}

.hero-content {
  position: relative;
  z-index: 1;
  padding-top: 120px;
  padding-bottom: 80px;
}

.hero h1 {
  font-size: 4.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  letter-spacing: -1px;
}

.hero-subtitle {
  font-family: var(--font-body);
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--accent);
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
}

.hero-description {
  font-size: 1.2rem;
  color: var(--silver-light);
  max-width: 560px;
  line-height: 1.8;
  margin-bottom: 2.5rem;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.pillar-icon {
  margin-bottom: 1.5rem;
}

.credential-card {
  padding: 2rem 1rem;
}

.credential-value {
  font-family: var(--font-heading);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--accent);
  margin-bottom: 0.5rem;
}

.credential-label {
  color: var(--silver);
  font-size: 0.875rem;
  font-weight: 500;
  letter-spacing: 0.5px;
}

@media (max-width: 768px) {
  .hero h1 {
    font-size: 2.75rem;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .hero-description {
    font-size: 1.05rem;
  }

  .hero-content {
    padding-top: 100px;
  }
}
</style>
